import React from 'react';
import { useEvent } from './useEvent.js';

export interface DropAreaState {
  over: boolean;
}

export interface DropAreaBond {
  onDragOver: React.DragEventHandler;
  onDragEnter: React.DragEventHandler;
  onDragLeave: React.DragEventHandler;
  onDrop: React.DragEventHandler;
  onPaste: React.ClipboardEventHandler;
}

export interface DropAreaOptions {
  onFiles?: (
    files: File[],
    event: React.DragEvent<Element> | React.ClipboardEvent<Element>,
  ) => void;
  onText?: (text: string, event: React.DragEvent<Element> | React.ClipboardEvent<Element>) => void;
  onUri?: (url: string, event: React.DragEvent<Element> | React.ClipboardEvent<Element>) => void;
}

export const useDrop = (options: DropAreaOptions): DropAreaState => {
  const { onFiles, onText, onUri } = options;

  const [over, setOver] = React.useState<boolean>(false);

  const process = React.useCallback(
    (
      dataTransfer: DataTransfer,
      event: React.DragEvent<Element> | React.ClipboardEvent<Element>,
    ) => {
      console.log('process', dataTransfer, event);

      const uri = dataTransfer.getData('text/uri-list');

      if (uri && onUri) {
        onUri(uri, event);
        return;
      }

      if (dataTransfer.files?.length && onFiles) {
        onFiles(Array.from(dataTransfer.files), event);
        return;
      }

      if ('clipboardData' in event && onText) {
        const text = event.clipboardData.getData('text');
        onText(text, event);
        return;
      }
    },
    [onFiles, onText, onUri],
  );

  useEvent('dragover', (event) => {
    event.preventDefault();
    setOver(true);
  });

  useEvent('dragenter', (event) => {
    event.preventDefault();
    setOver(true);
  });

  useEvent('dragleave', (event) => {
    event.preventDefault();
    setOver(false);
  });

  useEvent('dragexit', (event) => {
    event.preventDefault();
    setOver(false);
  });

  useEvent('drop', (event) => {
    event.preventDefault();
    setOver(false);
    process(event.dataTransfer, event);
  });

  return { over };
};
